import PlateStartFields, { EMPTY_START, startBody } from './culture/PlateStartFields';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import RecordAutocomplete from './RecordAutocomplete';
import FieldError, { InvalidWithin, errorIdFor, invalidBorder, invalidProps } from './FieldError';
import { collectErrors, hasErrors, required, useFieldErrors, validationMessage } from '../utils/formValidation';
import { focusElement } from '../utils/useFocusOnOpen';
import MediumOptions from './culture/MediumOptions';

// Grows ONE new plate from an existing slant. The slant's sample/external link,
// and therefore the plate's origin, is inherited server-side — this form only
// picks the slant and the growing conditions.
//
// `initialSlant` ({slant_id, slant_code, protocol}) opens the form on a slant
// already chosen, its medium pre-filled, as the home hub's "Revive" does; the
// picker still lets the reader choose another.
//
// Nothing is grown from a discarded or archived slant (routes/plates.py,
// 409 slant_not_usable): the picker does not offer one, and a refusal for one
// chosen elsewhere is said in the reader's words.
const growable = (s) => s.status !== 'discarded' && !s.is_archived;

export default function PlateFromSlantForm({ protocols, initialSlant = null, onCreated, onCancel }) {
  const { t } = useTranslation(['plates', 'common']);
  const [slant, setSlant]             = useState(initialSlant);
  const [protocol, setProtocol]       = useState(initialSlant?.protocol || '');
  const [temperature, setTemperature] = useState(25);
  const [notes, setNotes]             = useState('');
  const [start, setStart]             = useState(EMPTY_START);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  // Both are required by POST /plates/from-slant (routes/plates.py): a slant,
  // and a protocol -- given here, or the slant's own when it has one.
  const fieldErrs = useFieldErrors();
  // Mounted when the form opens: focus moves to it from the button that
  // opened it, which is elsewhere on the page.
  const headingRef = useRef(null);
  useEffect(() => { focusElement(headingRef.current); }, []);

  const protocolOptions = (!protocol || protocols.some(p => p.name === protocol))
    ? protocols
    : [{ protocol_id: '__current', name: protocol }, ...protocols];

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = collectErrors([
      ['slant', required(slant?.slant_id, 'fromSlant.slantRequired')],
      ['protocol', required(protocol, 'fromSlant.protocolRequired')],
    ]);
    if (hasErrors(errors)) {
      setError('');
      fieldErrs.show(errors);
      return;
    }
    fieldErrs.reset();
    setSaving(true);
    setError('');
    api.post('/plates/from-slant', {
      slant_id: slant.slant_id,
      protocol,
      temperature_c: parseFloat(temperature) || 25,
      notes: notes || null,
      ...startBody(start),
    })
      .then(res => onCreated(res.data.plates[0]))
      .catch(err => {
        const data = err.response?.data;
        setError(data?.code === 'slant_not_usable'
          ? t('fromSlant.slantNotUsable', { code: data.slant_code || slant.slant_code })
          : (data?.error || t('fromSlant.createFailed')));
      })
      .finally(() => setSaving(false));
  };

  const slantErr = fieldErrs.errors.slant;
  const protocolErr = fieldErrs.errors.protocol;

  return (
    <form onSubmit={handleSubmit} style={formStyle} ref={fieldErrs.containerRef} data-testid="from-slant-form">
      <h3 ref={headingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="from-slant-title">{t('fromSlant.title')}</h3>
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 4px' }}>{t('fromSlant.desc')}</p>
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 12px' }}>{t('common:validation.requiredNote')}</p>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: '0 0 12px' }}>{error}</p>}
      <div style={gridStyle}>
        <div style={stackStyle}>
          <label style={labelStyle}>
            {t('fromSlant.slantLabel')}
            <InvalidWithin required invalid={!!slantErr} errorId={errorIdFor('from-slant-slant')} data-field="slant">
              <RecordAutocomplete
                fetchUrl="/slants"
                filter={growable}
                getValue={s => s.slant_id}
                getLabel={s => s.slant_code}
                getSecondary={s => s.taxon_name}
                value={slant ? slant.slant_id : ''}
                onChange={id => { if (!id) setSlant(null); }}
                onSelect={s => {
                  setSlant(s);
                  setProtocol(s.protocol || '');
                  fieldErrs.clear(s.protocol ? (f) => f === 'slant' || f === 'protocol' : 'slant');
                }}
                placeholder={t('fromSlant.slantPlaceholder')}
              />
            </InvalidWithin>
          </label>
          <FieldError id={errorIdFor('from-slant-slant')} testId="from-slant-slant-error">{validationMessage(t, slantErr)}</FieldError>
        </div>
        <div style={labelStyle} data-field="protocol">
          <label htmlFor="from-slant-protocol">{t('fromSlant.protocolLabel')}</label>
          <select id="from-slant-protocol" value={protocol} aria-required="true"
                  onChange={e => { setProtocol(e.target.value); fieldErrs.clear('protocol'); }}
                  {...invalidProps(errorIdFor('from-slant-protocol'), !!protocolErr)}
                  style={{ ...inputStyle, ...invalidBorder(!!protocolErr) }} title={t('tip.fromSlantProtocol')}>
            <option value="">—</option>
            <MediumOptions protocols={protocolOptions} />
          </select>
          <FieldError id={errorIdFor('from-slant-protocol')} testId="from-slant-protocol-error">{validationMessage(t, protocolErr)}</FieldError>
        </div>
        <label style={labelStyle}>
          {t('fromSlant.temperatureLabel')}
          <input type="number" step="0.5" value={temperature} onChange={e => setTemperature(e.target.value)} style={inputStyle} title={t('tip.temperature')} />
        </label>
        <PlateStartFields value={start} labelStyle={labelStyle} inputStyle={inputStyle} testIdPrefix="from-slant-start"
                          onChange={patch => setStart(s => ({ ...s, ...patch }))} />
        <label style={{ ...labelStyle, gridColumn: 'span 2' }}>
          {t('fromSlant.notesLabel')}
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} style={inputStyle} title={t('tip.notes')} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="submit" disabled={saving} style={btnStyle('var(--c-primary)')} title={t('tip.fromSlantCreate')}>
          {saving ? t('common:saving') : t('fromSlant.createBtn')}
        </button>
        <button type="button" onClick={onCancel} style={btnStyle('var(--c-text-muted)')} title={t('tip.cancelForm')}>{t('common:cancel')}</button>
      </div>
    </form>
  );
}

const formStyle = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 20, marginBottom: 24 };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 16 };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, fontWeight: 500 };
// A labelled picker with its message under it, outside the <label> that names it.
const stackStyle = { display: 'flex', flexDirection: 'column', gap: 2 };
const inputStyle = { padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14, fontWeight: 400 };
const btnStyle = (bg) => ({ background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14 });
